import { ToolActivity } from "../message/mcp-tool-activity";
import { categorizeArtifactChange } from "./mcp-chat-change-category";
import type {
  ChangesSummary,
  ChangesSummaryItem,
  ChatOption,
  ParsedMessage,
  ParsedQuestionBlock,
  ParsedQuestionnaire,
  ParsedTaskPlan,
  TaskPlanStep
} from "./mcp-chat-types";

/**
 * Returns true when the option value represents a free-text "Other" choice.
 * Matches the explicit `__CUSTOM__` sentinel **and** natural labels such as
 * "Other", "Other (specify)", etc. that come from text-parsed options.
 *
 * NOTE: This is the legacy English-only detection. Prefer `isOtherSelection`
 * at any call site that has access to the parsed question — it checks the
 * server-marked `freeform` flag first and is language-agnostic.
 */
export function isOtherLikeValue(value: string): boolean {
  if (value === "__CUSTOM__") return true;
  // Strip optional lettered prefix (e.g. "D) ") from parsed questionnaire options
  const stripped = value.replace(/^[A-Za-z]\)\s*/, "");
  return /^other\b/i.test(stripped);
}

/**
 * Multi-language label heuristic for the "let me write my own" freeform option.
 * Used by `parseLetteredQuestionnaire` to set `freeform: true` on options
 * the LLM emitted as plain text (e.g. "E) Diğer (Ben Yazayım)") so the chat
 * UI renders a free-text input when the user picks them — matching the
 * structured `ui_common_ask_user_question` tool path that already supports
 * the flag natively (KOA-16873).
 *
 * Anchors at the start of the (already prefix-stripped) label so we do not
 * mis-match concrete options that happen to contain one of these words
 * inline ("Add Other Setting" must NOT trip the heuristic).
 */
export function looksLikeFreeformLabel(label: string): boolean {
  const stripped = label.replace(/^[A-Za-z]\)\s*/, "").trim();
  if (!stripped) return false;
  // Patterns are anchored at the start. Each language covers the canonical
  // word for "other" plus the typical "(let me specify / write my own)" tail.
  // `\b` is used for Latin scripts to avoid matching "Otherwise" / similar
  // word-continuation false positives. Non-Latin scripts (Cyrillic, CJK)
  // skip `\b` because JS regex `\b` is anchored on `[A-Za-z0-9_]` and never
  // fires after a Cyrillic / Han / Hangul / Kana character — keeping `\b`
  // there would silently reject every non-Latin freeform label.
  const FREEFORM_PATTERNS: RegExp[] = [
    /^other\b/i, // English
    /^let me specify\b/i,
    /^custom\b/i,
    /^diğer\b/i, // Turkish
    /^diger\b/i, // Turkish, i without dot
    /^kendim yazayım\b/i,
    /^kendim yazayim\b/i,
    /^ben yazayım\b/i,
    /^ben yazayim\b/i,
    /^andere\b/i, // German
    /^sonstige[sr]?\b/i,
    /^otro\b/i, // Spanish
    /^otra\b/i,
    /^otros\b/i,
    /^autre\b/i, // French
    /^altro\b/i, // Italian
    /^outro\b/i, // Portuguese
    /^inny\b/i, // Polish
    /^другое/i, // Russian (no \b — see comment above)
    /^その他/, // Japanese
    /^기타/, // Korean
    /^其他/ // Chinese (simplified/traditional shared)
  ];
  return FREEFORM_PATTERNS.some((re) => re.test(stripped));
}

/**
 * Build a `ChatOption`, auto-flagging the freeform "let me write my own"
 * variant when its label matches `looksLikeFreeformLabel`. Centralizes the
 * heuristic so every text-parser path produces the same shape and the
 * downstream `isOtherSelection` precedence (server flag → freeform → label
 * regex) works without each parser duplicating the check.
 */
function makeChatOption(label: string, value: string): ChatOption {
  if (looksLikeFreeformLabel(label)) {
    return { label, value, freeform: true };
  }
  return { label, value };
}

/**
 * Detects whether the user's selection on a clarifying question is a free-text
 * "Other" option. Uses precedence:
 *
 *   1. `__CUSTOM__` sentinel (legacy)
 *   2. The matching option's `freeform === true` flag (server-marked,
 *      language-agnostic — works for "Diğer", "Andere", "Otro", etc.)
 *   3. `isOtherLikeValue(value)` regex fallback (English label parsing,
 *      preserves backward compatibility for tool calls / legacy markdown
 *      questionnaires that were emitted before the freeform flag landed)
 *
 * Prefer this helper over `isOtherLikeValue` whenever the parsed question is
 * available — it is robust against label translation and future-proof.
 */
export function isOtherSelection(question: ParsedQuestionBlock | undefined, selectedValue: string): boolean {
  if (!selectedValue) return false;
  if (selectedValue === "__CUSTOM__") return true;
  const option = question?.options.find((o) => o.value === selectedValue);
  if (option?.freeform === true) return true;
  return isOtherLikeValue(selectedValue);
}

export function collapseExactDuplicateAssistantResponse(content: string): string {
  let normalized = content.replace(/\r\n/g, "\n").trim();
  if (!normalized) return content;

  while (true) {
    const duplicateMatch = normalized.match(/^([\s\S]+?)(?:\n\s*)+\1$/);
    if (!duplicateMatch) break;
    normalized = duplicateMatch[1].trim();
  }

  return normalized === content ? content : normalized;
}

function tryParseOptionLine(line: string): string | null {
  // 1) Foo / A) Foo / 1. Foo / A. Foo
  const indexed = line.match(/^\s*[-*•]?\s*(?:\d+|[A-Za-z])[).]\s+(.+?)\s*$/);
  if (indexed) return indexed[1].trim();

  // - Foo / * Foo / • Foo
  const bulleted = line.match(/^\s*[-*•]\s+(.+?)\s*$/);
  if (bulleted) return bulleted[1].trim();

  return null;
}

function parseOptionsLineList(raw: string): ChatOption[] | null {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length < 2 || lines.length > 15) return null;

  const options: ChatOption[] = [];
  for (const line of lines) {
    const text = tryParseOptionLine(line);
    if (!text) return null;
    options.push(makeChatOption(text, text));
  }

  return options;
}

function parseSectionedOptionsBlock(raw: string): ParsedQuestionnaire | null {
  const lines = raw.split(/\r?\n/);
  const questions: ParsedQuestionBlock[] = [];
  let currentHeader: string | null = null;
  let currentOptions: ChatOption[] = [];

  const flushSection = () => {
    if (currentHeader && currentOptions.length >= 2) {
      questions.push({ prompt: currentHeader, options: currentOptions });
    }
    currentHeader = null;
    currentOptions = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Check if this is an option line
    const optionText = tryParseOptionLine(trimmed);
    if (optionText) {
      currentOptions.push(makeChatOption(optionText, optionText));
      continue;
    }

    // Check if this is a section header (ends with ":" and is NOT an option)
    if (/^.+:\s*$/.test(trimmed)) {
      flushSection();
      currentHeader = trimmed.replace(/:\s*$/, "").trim();
      continue;
    }

    // Unrecognized line — bail out
    return null;
  }

  flushSection();

  if (questions.length === 0) return null;

  return { intro: "", questions, outro: "" };
}

function parseBracketOptionsBlock(raw: string): ParsedQuestionnaire | null {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return null;

  const questions: ParsedQuestionBlock[] = [];

  for (const line of lines) {
    // Match: key: [value1, value2, ...]
    const bracketMatch = line.match(/^(.+?):\s*\[(.+)\]\s*$/);
    if (!bracketMatch) return null;

    const prompt = bracketMatch[1].trim();
    const inner = bracketMatch[2].trim();
    if (!prompt || !inner) return null;

    // Prefer comma separator, fall back to " / " (spaced slash)
    let parts: string[];
    if (inner.includes(",")) {
      parts = inner
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    } else if (inner.includes(" / ")) {
      parts = inner
        .split(" / ")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    } else {
      return null;
    }

    if (parts.length < 2) return null;

    questions.push({
      prompt,
      options: parts.map((p) => makeChatOption(p, p))
    });
  }

  if (questions.length === 0) return null;

  return { intro: "", questions, outro: "" };
}

function parsePipeSeparatedBlock(raw: string): ParsedQuestionnaire | null {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return null;

  const questions: ParsedQuestionBlock[] = [];

  for (const line of lines) {
    const colonIndex = line.indexOf(":");
    if (colonIndex < 1) return null;

    const label = line.substring(0, colonIndex).trim();
    const rest = line.substring(colonIndex + 1).trim();
    if (!label || !rest || !rest.includes("|")) return null;

    const parts = rest
      .split("|")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (parts.length < 2) return null;

    questions.push({
      prompt: label,
      options: parts.map((p) => makeChatOption(p, p))
    });
  }

  if (questions.length === 0) return null;

  return { intro: "", questions, outro: "" };
}

interface ParsedOptionsResult {
  options: ChatOption[];
  multiSelect?: boolean;
}

function parseOptionsArray(raw: string): ParsedOptionsResult | null {
  try {
    const parsed = JSON.parse(raw);

    // Object wrapper: { multiSelect?: boolean, options: [...] }
    if (parsed && !Array.isArray(parsed) && Array.isArray(parsed.options)) {
      const opts = parsed.options;
      if (
        opts.length < 2 ||
        opts.length > 15 ||
        !opts.every((o: any) => typeof o.label === "string" && typeof o.value === "string")
      ) {
        return null;
      }
      return { options: opts as ChatOption[], multiSelect: !!parsed.multiSelect };
    }

    // Plain array: [{ label, value }, ...]
    if (
      !Array.isArray(parsed) ||
      parsed.length < 2 ||
      parsed.length > 15 ||
      !parsed.every((o: any) => typeof o.label === "string" && typeof o.value === "string")
    ) {
      return null;
    }
    return { options: parsed as ChatOption[] };
  } catch {
    const list = parseOptionsLineList(raw);
    return list ? { options: list } : null;
  }
}

function splitIntroAndFirstPrompt(segment: string): { intro: string; prompt: string } {
  const trimmed = segment.trim();
  if (!trimmed) return { intro: "", prompt: "" };

  const lines = trimmed.split(/\r?\n/);
  if (lines.length <= 1) return { intro: "", prompt: trimmed };

  let lastNonEmptyLine = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].trim()) {
      lastNonEmptyLine = i;
      break;
    }
  }

  if (lastNonEmptyLine <= 0) return { intro: "", prompt: trimmed };

  const prompt = lines[lastNonEmptyLine].trim();
  const intro = lines.slice(0, lastNonEmptyLine).join("\n").trim();

  return { intro, prompt: prompt || trimmed };
}

/**
 * Split inline `A) ... B) ... C) ...` options onto separate lines so the
 * lettered-questionnaire parser can recognize them. Some LLM outputs cram
 * the prompt and all options onto a single line:
 *
 *   "Hangi seçenek? A) Foo B) Bar C) Baz"
 *
 * After preprocessing this becomes:
 *
 *   "Hangi seçenek?\nA) Foo\nB) Bar\nC) Baz"
 *
 * The split is conservative: only triggers when at least 2 lettered options
 * are seen on the same line in increasing letter sequence (A→B, B→C, …).
 * Avoids splitting prose that legitimately contains "A) something" mid-text
 * (e.g. citation footnotes), since those don't form an A/B/C/... run.
 */
function splitInlineLetteredOptions(content: string): string {
  const lines = content.split(/\r?\n/);
  const out: string[] = [];
  // Match a candidate run: ` X) text` where X is A-Z. Use a global regex with
  // capture groups; positions are needed to slice the surrounding text.
  const inlineOptRegex = /\s+([A-Z])\)\s+/g;
  for (const line of lines) {
    inlineOptRegex.lastIndex = 0;
    const matches: { letter: string; index: number; matchLength: number }[] = [];
    let m: RegExpExecArray | null;
    while ((m = inlineOptRegex.exec(line)) !== null) {
      matches.push({ letter: m[1], index: m.index, matchLength: m[0].length });
    }
    if (matches.length < 2) {
      out.push(line);
      continue;
    }
    // Verify the letters form a contiguous A→B→C→... run starting from the
    // first detected letter. Otherwise the matches likely come from prose.
    let isRun = true;
    for (let i = 1; i < matches.length; i++) {
      if (matches[i].letter.charCodeAt(0) !== matches[i - 1].letter.charCodeAt(0) + 1) {
        isRun = false;
        break;
      }
    }
    if (!isRun) {
      out.push(line);
      continue;
    }
    // Slice the line: prefix, then each `LETTER) text` segment on its own line.
    const prefix = line.slice(0, matches[0].index).trimEnd();
    if (prefix) out.push(prefix);
    for (let i = 0; i < matches.length; i++) {
      // Skip the single whitespace char that the regex consumed before LETTER.
      const segStart = matches[i].index + 1;
      const segEnd = i + 1 < matches.length ? matches[i + 1].index : line.length;
      const seg = line.slice(segStart, segEnd).trim();
      if (seg) out.push(seg);
    }
  }
  return out.join("\n");
}

function parseLetteredQuestionnaire(content: string): ParsedQuestionnaire | null {
  const lines = splitInlineLetteredOptions(content).split(/\r?\n/);
  const questions: ParsedQuestionBlock[] = [];
  const questionRegex = /^\s*(\d+)[).]\s*(.+?)\s*:?\s*$/;
  // Also match markdown headers as question prompts: ## Question text
  const headerQuestionRegex = /^\s*#{1,3}\s+(.+?)\s*$/;
  // Plain-prose question line — ends with `?` or `:` and is non-empty. Used
  // as a last resort when the LLM emits a bare prompt without numbering or
  // markdown-header markup. We only accept it when a lettered option run
  // follows on subsequent lines (validated by `hasOptionsAfter`), so we
  // don't accidentally treat random colon-terminated sentences as prompts.
  const plainPromptRegex = /^\s*(.+?[?:])\s*$/;
  const optionRegex = /^\s*[-*•]?\s*([A-Za-z])[).]\s*(.+?)\s*$/;

  /** Check if a line looks like a question heading (numbered OR markdown header OR plain prose). */
  function matchQuestion(line: string): string | null {
    const numbered = line.match(questionRegex);
    if (numbered) return numbered[2].trim();
    const header = line.match(headerQuestionRegex);
    if (header) return header[1].trim();
    // Plain prose: only count if it isn't itself a lettered option.
    if (optionRegex.test(line)) return null;
    const plain = line.match(plainPromptRegex);
    if (plain) {
      // Strip the trailing punctuation marker so the rendered prompt reads
      // naturally — `?` is kept (it conveys a question), `:` is dropped.
      const raw = plain[1].trim();
      return raw.endsWith(":") ? raw.slice(0, -1).trim() : raw;
    }
    return null;
  }

  /** Check if a line starts a new question. */
  function isQuestionLine(line: string): boolean {
    if (questionRegex.test(line) || headerQuestionRegex.test(line)) return true;
    if (optionRegex.test(line)) return false;
    return plainPromptRegex.test(line);
  }

  // Look ahead to verify a question candidate is followed by lettered options
  function hasOptionsAfter(startIdx: number): boolean {
    for (let j = startIdx + 1; j < lines.length; j++) {
      const trimmed = lines[j].trim();
      if (trimmed === "") continue;
      return optionRegex.test(trimmed);
    }
    return false;
  }

  let firstQuestionIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (isQuestionLine(lines[i]) && hasOptionsAfter(i)) {
      firstQuestionIndex = i;
      break;
    }
  }
  if (firstQuestionIndex < 0) return null;

  const intro = lines.slice(0, firstQuestionIndex).join("\n").trim();
  let cursor = firstQuestionIndex;
  let outroStartIndex = lines.length;

  while (cursor < lines.length) {
    const prompt_ = matchQuestion(lines[cursor]);
    if (!prompt_) {
      outroStartIndex = cursor;
      break;
    }

    let prompt = prompt_ || `Question ${questions.length + 1}`;
    // Detect multi-select hint and strip it from the displayed prompt
    const multiSelectHint = /\s*\(select\s+all\s+that\s+apply\)\s*/i;
    const isMultiSelect = multiSelectHint.test(prompt);
    if (isMultiSelect) {
      prompt = prompt.replace(multiSelectHint, "").trim() || `Question ${questions.length + 1}`;
    }
    cursor += 1;

    const options: ChatOption[] = [];
    let hitOutro = false;

    while (cursor < lines.length) {
      if (isQuestionLine(lines[cursor]) && hasOptionsAfter(cursor)) break;

      const optionMatch = lines[cursor].match(optionRegex);
      if (optionMatch) {
        const code = optionMatch[1].toUpperCase();
        // Strip markdown bold/italic markers from option text
        const text = optionMatch[2].trim().replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1");
        if (text) {
          const normalized = `${code}) ${text}`;
          options.push(makeChatOption(normalized, normalized));
        }
        cursor += 1;
        continue;
      }

      if (lines[cursor].trim() === "") {
        cursor += 1;
        continue;
      }

      if (options.length >= 2) {
        hitOutro = true;
        break;
      }
      return null;
    }

    if (options.length < 2 || options.length > 15) return null;
    questions.push({ prompt, options, multiSelect: isMultiSelect || undefined });

    if (hitOutro) {
      outroStartIndex = cursor;
      break;
    }
  }

  if (questions.length < 1) return null;

  const outro = lines.slice(outroStartIndex).join("\n").trim();

  return { intro, questions, outro };
}

/**
 * The directive that rides an approved plan back to the server (KOA-18373).
 *
 * It replaces `"Execute the plan above. Use it as a guide for direction and
 * intent - adapt based on actual results to achieve the best possible
 * outcome."`, whose second clause authorised deviating from the very thing the
 * user had just approved. Reporting a step that could not be done is honest;
 * quietly building something else is not.
 *
 * Deliberately terse and language-neutral — the approved plan's own markdown
 * follows it and carries the conversation language.
 */
export const PLAN_APPROVED_DIRECTIVE: string =
  "The user approved the plan below. Implement it as approved: do every step, do not re-plan it, " +
  "and do not substitute a different approach. If a step turns out to be impossible, do the rest and " +
  "report that step as unfinished instead of replacing it.";

/**
 * KOA-18304 — the two answers to the server's Designer capability question.
 *
 * These literals are the wire. The server writes them as the `value` of the two
 * chips (mcp-chat-server `canned-replies.ts`, `DESIGNER_CHOICE_*`), and a chip
 * click submits the value verbatim, so the panel matches them exactly and never
 * parses them. If either side drifts the chip degrades into an ordinary text
 * message — and for the "stay in Designer" answer that means the server asks the
 * same question about the same request again, forever. Pinned by tests on both
 * sides; they live here rather than on the panel component so a test can import
 * them without mounting it.
 */
export const DESIGNER_CHOICE_CONTINUE: string = "__kuika_designer_continue__";
export const DESIGNER_CHOICE_SWITCH: string = "__kuika_switch_builder__";

export function parseAssistantMessage(content: string): ParsedMessage {
  // Detect <task_plan> blocks first
  const taskPlan = parseTaskPlan(content);
  if (taskPlan) {
    return {
      textBefore: taskPlan.textBefore,
      options: null,
      textAfter: taskPlan.textAfter,
      questionnaire: null,
      taskPlan
    };
  }

  // Detect ```options or ```options:multi fenced blocks
  const regex = /```options(?::multi)?\s*\n([\s\S]*?)```/g;
  const matches: Array<{ start: number; end: number; options: ChatOption[]; multiSelect: boolean }> = [];

  let match: RegExpExecArray | null = null;
  while ((match = regex.exec(content)) !== null) {
    const fullTag = match[0];
    const isMultiTag = /^```options:multi/i.test(fullTag);
    const parsed = parseOptionsArray(match[1]);
    if (!parsed) {
      // Try alternative block formats before giving up
      const questionnaire =
        parseBracketOptionsBlock(match[1]) || parseSectionedOptionsBlock(match[1]) || parsePipeSeparatedBlock(match[1]);
      if (questionnaire) {
        const textBefore = content.substring(0, match.index).trimEnd();
        const textAfter = content.substring(match.index + match[0].length).trimStart();

        if (questionnaire.questions.length === 1) {
          // Single section → simple option chips with header as bold prefix
          const q = questionnaire.questions[0];
          const prefixedBefore = textBefore ? `${textBefore}\n\n**${q.prompt}**` : `**${q.prompt}**`;
          return {
            textBefore: prefixedBefore,
            options: q.options,
            textAfter,
            questionnaire: null,
            multiSelect: isMultiTag,
            taskPlan: null
          };
        }

        // Multiple sections → questionnaire wizard
        if (isMultiTag) {
          questionnaire.questions.forEach((q) => {
            q.multiSelect = true;
          });
        }
        return {
          textBefore: content,
          options: null,
          textAfter: "",
          questionnaire: {
            intro: textBefore,
            questions: questionnaire.questions,
            outro: textAfter
          },
          taskPlan: null
        };
      }
      return { textBefore: content, options: null, textAfter: "", questionnaire: null, taskPlan: null };
    }
    const blockMultiSelect = isMultiTag || !!parsed.multiSelect;
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      options: parsed.options,
      multiSelect: blockMultiSelect
    });
  }

  if (matches.length === 0) {
    const lettered = parseLetteredQuestionnaire(content);
    if (lettered) {
      // Single question → simple option chips (no wizard overhead)
      // Exception: multi-select single questions keep the wizard for Submit button
      if (lettered.questions.length === 1 && !lettered.questions[0].multiSelect) {
        const q = lettered.questions[0];
        const prefixedBefore = lettered.intro ? `${lettered.intro}\n\n**${q.prompt}**` : `**${q.prompt}**`;
        return {
          textBefore: prefixedBefore,
          options: q.options,
          textAfter: lettered.outro,
          questionnaire: null,
          taskPlan: null
        };
      }
      return {
        textBefore: content,
        options: null,
        textAfter: "",
        questionnaire: lettered,
        taskPlan: null
      };
    }
    return { textBefore: content, options: null, textAfter: "", questionnaire: null, taskPlan: null };
  }

  if (matches.length === 1) {
    const single = matches[0];
    const textBefore = content.substring(0, single.start).trimEnd();
    const textAfter = content.substring(single.end).trimStart();
    return {
      textBefore,
      options: single.options,
      textAfter,
      questionnaire: null,
      multiSelect: single.multiSelect,
      taskPlan: null
    };
  }

  let cursor = 0;
  let intro = "";
  const questions: ParsedQuestionBlock[] = [];

  for (let i = 0; i < matches.length; i++) {
    const block = matches[i];
    const segment = content.substring(cursor, block.start).trim();
    if (i === 0) {
      const first = splitIntroAndFirstPrompt(segment);
      intro = first.intro;
      questions.push({
        prompt: first.prompt || `Question ${i + 1}`,
        options: block.options,
        multiSelect: block.multiSelect || undefined
      });
    } else {
      questions.push({
        prompt: segment || `Question ${i + 1}`,
        options: block.options,
        multiSelect: block.multiSelect || undefined
      });
    }
    cursor = block.end;
  }

  const last = matches[matches.length - 1];
  const outro = content.substring(last.end).trim();

  return {
    textBefore: content,
    options: null,
    textAfter: "",
    questionnaire: {
      intro,
      questions,
      outro
    },
    taskPlan: null
  };
}

export function detectPlanMessage(content: string): boolean {
  return /^##\s+Plan:/m.test(content) && /^###\s+Steps/m.test(content);
}

/**
 * Detects the Business Analyst schematic spec (KOA-17628 / KOA-17797).
 * Product H2 is always `## Spec:`; section titles may be localized.
 * Also accepts older messages that still embed `<!-- kuika-spec -->` anchors.
 */
export function detectSpecMessage(content: string): boolean {
  if (/<!--\s*kuika-spec\s*-->/i.test(content)) return true;
  if (!/^##\s+Spec:/m.test(content)) return false;
  return /^###\s+\S+/m.test(content);
}

/** Strip machine anchors if present so they never show in the chat bubble. */
export function stripSpecMachineAnchors(content: string): string {
  return content
    .replace(/\s*<!--\s*kuika-spec(?:-[a-z]+)?\s*-->/gi, "")
    .replace(/\s*<!--\s*kuika-change-delta\s+[^>]*-->/gi, "")
    .replace(/[ \t]+\n/g, "\n");
}

/**
 * KOA-18422 — the change-plan proposal card.
 *
 * A change plan ends with an anchor naming the delta it proposes
 * (`<!-- kuika-change-delta <id> -->`). Approving sends that ID back as a
 * structured intent, so what gets applied is what was SHOWN — unlike the BA spec
 * card, which re-drives the turn with its own markdown as the prompt and
 * therefore asks the pipeline to read the request a second time.
 *
 * Detection is the anchor and NOTHING else: no header match, no section shape.
 * A plan whose delta could not be recorded carries no anchor and correctly gets
 * no buttons — there would be nothing for them to approve.
 */
export function parseChangeDeltaId(content: string): string | null {
  const match = content.match(/<!--\s*kuika-change-delta\s+([A-Za-z0-9-]{8,})\s*-->/i);
  return match ? match[1] : null;
}

/**
 * KOA-18422 — is this send a DECISION on a change plan?
 *
 * Approve and Revise are the only sends whose payload is not text: the card
 * carries the delta's id and the server applies what was shown. Everything else
 * about a send — the empty guard, the input box, the visible bubble — assumes a
 * message, so the one place that assumption is false has to be named once and
 * asked, rather than re-derived at each site.
 */
export function isChangeDecisionIntent(intent: string | undefined): boolean {
  return intent === "apply_change_delta" || intent === "discard_change_delta";
}

/** What a send is carrying. Text is one of five ways to carry something, not the only one. */
export interface SendPayload {
  text: string;
  hasImages: boolean;
  hasFiles: boolean;
  attachmentCount: number;
  hasSegments: boolean;
  /** Structured intent, when the send came from a button rather than the composer. */
  intent?: string;
}

/**
 * Is there anything to send?
 *
 * The guard this replaces read `if (!text && !images && !files && !attachments &&
 * !segments) return`, which is right for the composer and wrong for a button: on
 * 2026-09-06 approving a change plan flipped the card to "approved" and sent
 * NOTHING, because a decision deliberately carries no text — the request never
 * reached the server and the turn simply never happened. A structured decision IS
 * the payload; the empty message is not an empty send.
 */
export function hasSendablePayload(payload: SendPayload): boolean {
  if (isChangeDecisionIntent(payload.intent)) return true;
  return (
    payload.text.length > 0 ||
    payload.hasImages ||
    payload.hasFiles ||
    payload.attachmentCount > 0 ||
    payload.hasSegments
  );
}

/**
 * Parse a plan block from message content into structured steps.
 * Supports two formats:
 * 1. `<task_plan>` tags (legacy supervisor format)
 * 2. `## Plan:` / `### Steps` markdown (current PLAN mode format)
 */
export function parseTaskPlan(content: string): ParsedTaskPlan | null {
  // Try <task_plan> tags first
  const tagMatch = content.match(/<task_plan>([\s\S]*?)<\/task_plan>/);
  if (tagMatch) {
    return parseTaskPlanFromBody(
      tagMatch[1],
      content.slice(0, content.indexOf("<task_plan>")).trim(),
      content.slice(content.indexOf("</task_plan>") + "</task_plan>".length).trim()
    );
  }

  // Try ## Plan: / ### Steps markdown format
  if (!detectPlanMessage(content)) return null;

  const stepsHeaderMatch = content.match(/^###\s+Steps\s*$/m);
  if (!stepsHeaderMatch || stepsHeaderMatch.index == null) return null;

  const stepsStart = stepsHeaderMatch.index + stepsHeaderMatch[0].length;
  // Steps section ends at next ### header, --- separator, or end of content
  const nextSection = content.slice(stepsStart).match(/\n###\s+|\n---\s*$/m);
  const stepsBlock =
    nextSection && nextSection.index != null
      ? content.slice(stepsStart, stepsStart + nextSection.index)
      : content.slice(stepsStart);

  const textBefore = content.slice(0, stepsHeaderMatch.index).trim();
  const textAfter =
    nextSection && nextSection.index != null ? content.slice(stepsStart + nextSection.index).trim() : "";

  return parseTaskPlanFromBody(stepsBlock, textBefore, textAfter);
}

function parseTaskPlanFromBody(body: string, textBefore: string, textAfter: string): ParsedTaskPlan | null {
  const steps: TaskPlanStep[] = [];
  let stepCounter = 1;

  for (const line of body.split("\n")) {
    const trimmed = line.trim();
    // Match checkbox format: - [x] 1. Description or - [ ] Description (no number)
    const checkboxMatch = trimmed.match(/^-\s*\[([x ])\]\s*(?:(\d+)\.\s+)?(.+?)(?:\s*->\s*(\w+))?\s*$/i);
    if (checkboxMatch) {
      const index = checkboxMatch[2] ? parseInt(checkboxMatch[2], 10) : stepCounter++;
      steps.push({
        index,
        description: checkboxMatch[3].trim(),
        agent: checkboxMatch[4] || null,
        completed: checkboxMatch[1].toLowerCase() === "x"
      });
      continue;
    }
    // Match plain numbered steps like "1. Do something -> AgentName"
    const stepMatch = trimmed.match(/^(\d+)\.\s+(.+?)(?:\s*->\s*(\w+))?\s*$/);
    if (stepMatch) {
      steps.push({
        index: parseInt(stepMatch[1], 10),
        description: stepMatch[2].trim(),
        agent: stepMatch[3] || null,
        completed: false
      });
      stepCounter = parseInt(stepMatch[1], 10) + 1;
    }
  }

  if (steps.length === 0) return null;
  return { steps, textBefore, textAfter };
}

/** Returns the label for a step's index/check column. */
export function formatStepLabel(index: number, totalSteps: number, completed: boolean): string {
  if (completed) return "✓";
  if (totalSteps <= 1) return "";
  return String(index);
}

/** Returns the step count text for the plan header. */
export function formatStepCount(completedCount: number, totalSteps: number): string {
  if (completedCount > 0) return `${completedCount}/${totalSteps}`;
  return totalSteps === 1 ? "1 step" : `${totalSteps} steps`;
}

/**
 * Merges step completion state from the backend's planJson into a parsed plan.
 * Returns the original plan if planJson is null/undefined/malformed.
 */
export function mergePlanCompletion(plan: ParsedTaskPlan, planJson: string | null | undefined): ParsedTaskPlan {
  if (!planJson) return plan;

  try {
    const doc = JSON.parse(planJson);
    if (!doc.steps || !Array.isArray(doc.steps)) return plan;

    // When the plan is completed, force all steps checked
    if (doc.status === "completed") {
      return {
        ...plan,
        steps: plan.steps.map((step) => ({ ...step, completed: true }))
      };
    }

    const completionMap = new Map<number, boolean>();
    for (const step of doc.steps) {
      if (typeof step.index === "number" && typeof step.completed === "boolean") {
        completionMap.set(step.index, step.completed);
      }
    }

    if (completionMap.size === 0) return plan;

    return {
      ...plan,
      steps: plan.steps.map((step) => ({
        ...step,
        completed: completionMap.get(step.index) ?? step.completed
      }))
    };
  } catch {
    return plan;
  }
}

export function buildChangesSummary(activities: ToolActivity[]): ChangesSummary {
  const groups = new Map<string, string[]>();
  let totalMutations: number = 0;

  for (const a of activities) {
    if (a.status !== "completed") continue;

    const operation = typeof a.input?.operation === "string" ? a.input.operation : undefined;
    const isSpecialist = !!a.specialistAgent || a.toolName.startsWith("specialist:");
    const category = categorizeArtifactChange({
      toolName: a.toolName,
      operation,
      isMutating: a.isMutating,
      isSpecialist
    });
    if (!category) continue;

    totalMutations += 1;
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category)!.push(a.description);
  }

  if (totalMutations === 0) return { totalMutations: 0, items: [] };

  const items: ChangesSummaryItem[] = [];
  for (const [category, details] of groups) {
    items.push({ category, count: details.length, details: details.slice(0, 5) });
  }

  return { totalMutations, items };
}
